<template>
  <v-card class="mt-5" outlined>
    <v-card-title>
      <v-icon color="deep-purple" class="mr-2">mdi-account-heart</v-icon>
      Customer Retention
      <v-spacer></v-spacer>
      <span v-if="dashboard" class="caption grey--text mr-3">
        {{ dashboard.week.label }} &middot; generated {{ dashboard.generated_at }}
      </span>
      <v-btn small icon :loading="loading" @click="refresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text v-if="dashboard">
      <v-row>
        <!-- ============ LEFT: SCORECARD ============ -->
        <v-col cols="12" md="8">
          <div class="crm-scorecard rounded-lg">
            <div class="crm-scorecard__header px-3 py-2">
              CUSTOMER RETENTION &nbsp;·&nbsp; Weekly Scorecard
            </div>
            <div class="crm-scorecard__subhead px-3 py-1">
              Funnel: repeat business &nbsp;(top to bottom)
            </div>
            <v-simple-table dense class="crm-scorecard__table">
              <thead>
                <tr>
                  <th style="width: 32px">#</th>
                  <th>KPI</th>
                  <th class="hidden-sm-and-down">How it's measured</th>
                  <th style="width: 110px">Cadence</th>
                  <th class="text-center" style="width: 120px">This week</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(kpi, i) in dashboard.scorecard"
                  :key="kpi.id"
                  :class="{ 'crm-row--accent': kpi.id === 'repeat_revenue' }"
                >
                  <td class="font-weight-bold">{{ i + 1 }}</td>
                  <td class="font-weight-bold">{{ kpi.label }}</td>
                  <td class="hidden-sm-and-down grey--text text--darken-1">
                    {{ kpi.measure }}
                  </td>
                  <td class="grey--text text--darken-1">{{ kpi.cadence }}</td>
                  <td class="text-center">
                    <div
                      class="font-weight-bold"
                      :class="statusTextClass(kpi.status)"
                    >
                      {{ kpi.highlight || kpi.value }}
                    </div>
                    <div class="caption grey--text">{{ kpi.detail }}</div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- This-week worklist -->
          <div class="section-label mt-4">
            <v-icon x-small>mdi-clipboard-check-outline</v-icon>
            This week's worklist
          </div>
          <v-row dense>
            <v-col
              v-for="tile in worklistTiles"
              :key="tile.label"
              cols="6"
              sm="4"
              md="2"
            >
              <v-card outlined class="text-center py-3" :color="tile.bg">
                <div class="text-h5 font-weight-bold" :class="tile.text">
                  {{ tile.value }}
                </div>
                <div class="caption grey--text text--darken-1">
                  {{ tile.label }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Per-branch split -->
          <div class="section-label mt-4">
            <v-icon x-small>mdi-source-branch</v-icon>
            This week by branch
          </div>
          <v-simple-table dense class="bordered-table rounded-lg">
            <thead>
              <tr>
                <th>Branch</th>
                <th class="text-center">Follow-ups (done/due)</th>
                <th class="text-center">Maintenance (done/due)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in dashboard.branches" :key="b.branch_id">
                <td class="font-weight-medium">{{ b.branch_name }}</td>
                <td class="text-center">
                  {{ b.follow_ups_done }} / {{ b.follow_ups_due }}
                </td>
                <td class="text-center">
                  {{ b.maintenance_done }} / {{ b.maintenance_due }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>

        <!-- ============ RIGHT: ACCOUNTABILITIES ============ -->
        <v-col cols="12" md="4">
          <v-card outlined class="crm-accountabilities rounded-lg fill-height">
            <v-card-text>
              <div class="text-h6 font-weight-bold mb-2">
                Customer Retention Manager
              </div>
              <div
                v-for="member in team"
                :key="member.name"
                class="d-flex align-center mb-1"
              >
                <span class="font-weight-bold mr-2">{{ member.name }}</span>
                <v-chip x-small color="deep-purple lighten-4">{{
                  member.branch
                }}</v-chip>
              </div>

              <v-divider class="my-3"></v-divider>

              <ul class="crm-accountabilities__list">
                <li v-for="(duty, i) in accountabilities" :key="i">{{ duty }}</li>
              </ul>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text v-else class="text-center grey--text">
      {{ loading ? "Loading customer retention scorecard..." : "No data available." }}
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <p class="caption grey--text text-right mb-0">
        customerRetentionDashboard.vue
      </p>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  name: "CustomerRetentionDashboard",
  data() {
    return {
      // Org accountabilities for the role (Operations Manual Part 16).
      team: [
        { name: "Arniel Puyales", branch: "Cantilan" },
        { name: "Louiebert Espinoza", branch: "Tandag" },
      ],
      accountabilities: [
        "Keeps the installed systems register",
        "Runs the free quarterly maintenance",
        "Follows up customers on schedule (after purchase, Starlink reminders), logging every contact",
        "Makes the month-13 offer",
        "Sends repeat orders and referrals to Sales",
        "Reports retention numbers weekly",
      ],
    };
  },
  created() {
    this.refresh();
  },
  computed: {
    ...mapGetters({
      dashboard: "customer_retention_dashboard/dashboard",
      loading: "customer_retention_dashboard/loading",
    }),
    worklistTiles() {
      const w = this.dashboard ? this.dashboard.worklist : {};
      return [
        {
          label: "Follow-ups due",
          value: w.follow_ups_due || 0,
          bg: "blue lighten-5",
          text: "blue--text text--darken-2",
        },
        {
          label: "Maintenance due",
          value: w.maintenance_due || 0,
          bg: "deep-purple lighten-5",
          text: "deep-purple--text",
        },
        {
          label: "Overdue visits",
          value: w.overdue_visits || 0,
          bg: w.overdue_visits ? "red lighten-5" : undefined,
          text: w.overdue_visits ? "red--text text--darken-2" : "grey--text",
        },
        {
          label: "Pending setup",
          value: w.pending_setup || 0,
          bg: w.pending_setup ? "orange lighten-5" : undefined,
          text: w.pending_setup ? "orange--text text--darken-3" : "grey--text",
        },
        {
          label: "Month-13 offers",
          value: w.month13_offers || 0,
          bg: "teal lighten-5",
          text: "teal--text text--darken-2",
        },
      ];
    },
  },
  methods: {
    ...mapActions({
      fetch_dashboard: "customer_retention_dashboard/fetch_dashboard",
    }),
    async refresh() {
      try {
        await this.fetch_dashboard();
      } catch (error) {
        var status = error.response && error.response.status;
        var serverMessage =
          error.response && error.response.data && error.response.data.message;
        this.$alert({
          title: "Error",
          message:
            "Unable to load the customer retention scorecard." +
            (status ? " (HTTP " + status + ")" : "") +
            (serverMessage ? " " + serverMessage : ""),
          color: "red",
          icon: "$error",
        });
      }
    },
    statusTextClass(status) {
      return (
        {
          good: "green--text text--darken-1",
          warn: "orange--text text--darken-2",
          bad: "red--text text--darken-1",
          neutral: "grey--text",
        }[status] || "grey--text"
      );
    },
  },
};
</script>

<style scoped>
.crm-scorecard {
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.crm-scorecard__header {
  background: #4a237a;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.crm-scorecard__subhead {
  background: #efe9f6;
  color: #5e35b1;
  font-weight: 600;
  font-size: 0.8rem;
}
.crm-scorecard__table >>> tbody tr.crm-row--accent {
  background: #fff8e1;
}
.crm-accountabilities__list {
  padding-left: 18px;
  margin: 0;
}
.crm-accountabilities__list li {
  margin-bottom: 6px;
  line-height: 1.35;
}
/* Dark-theme adjustments */
.theme--dark .crm-scorecard {
  border-color: rgba(255, 255, 255, 0.12);
}
.theme--dark .crm-scorecard__subhead {
  background: #311b4f;
  color: #d1c4e9;
}
.theme--dark .crm-scorecard__table >>> tbody tr.crm-row--accent {
  background: rgba(255, 193, 7, 0.12);
}
</style>
